// @ts-check
// Retroalimentación pedagógica GRADUADA y DETERMINISTA (roadmap §10, niveles
// 1-3). Módulo PURO de la capa `features`: sin DOM, sin red, sin estado.
//
// ## Por qué esto NO necesita IA ni backend (ADR-0006, DEC-006)
// El §10 pide 9 niveles de ayuda crecientes. Los tres primeros son DERIVABLES
// de datos que la aplicación ya tiene, así que se resuelven en la capa
// determinista, la única con autoridad sobre la nota (ADR-0006 punto 1):
//   1. "hay un problema"            -- gratis, deliberadamente vago.
//   2. "qué falla exactamente"      -- gratis, con DIAGNÓSTICO de la diferencia
//                                      (mayúsculas, espacios, número, longitud).
//      En pseudocódigo y Python se diagnostica con la regla del corrector
//      (DEC-119, `normalizar`): lo que no cuenta para la nota no se señala.
//   3. "casi la solución"           -- PENALIZA la puntuación (ver `cuesta`).
// La IA queda para los niveles superiores (explicar el error sobre el código
// concreto del alumno, solución parcial), que sí son cualitativos.
//
// El escalón 3 cuesta puntos por decisión de producto del usuario (2026-07-31):
// una ayuda que casi resuelve el ejercicio no puede ser gratis, o el alumno la
// pide siempre y deja de pensar. El cobro NO lo hace este módulo: se limita a
// MARCAR el peldaño con `cuesta: true`; quien lo muestre debe cobrarlo contra
// el endpoint de pista, que es el que descuenta en SERVIDOR (DEC-064/DEC-065).
//
// Consecuencia práctica de ser determinista: coste de IA 0, respuesta
// instantánea, reproducible, y NADA sale de la infraestructura -- por tanto NO
// depende de DEC-011 (marco legal de menores, ABIERTA).
//
// ## De dónde salen los datos (y por qué no filtran nada, DEC-016/SEC-R5)
// Todo lo que consume este módulo YA está en el cliente y YA es público:
//   - Los casos del resultado de corrección (`entrada`/`salidaEsperada`/
//     `salidaObtenida`/`correcto`), que solo contienen casos VISIBLES: los
//     esquemas de respuesta no pueden alojar casos ocultos, rúbrica,
//     `contenido_docente` ni `solucion_diagrama_ir` (ver docstrings de
//     `app.schemas.autocorrection` y `app.schemas.text_correction`).
//   - `contenido_pedagogico` (DEC-015): `objetivos`, `conceptos_previos` y
//     `errores_frecuentes`, redactados por quien escribió el ejercicio y ya
//     servidos al estudiante en el enunciado.
// Este módulo NO inventa contenido nuevo ni deduce la solución: reordena y
// dosifica material que el alumno ya podía leer, que es justo lo que pide el
// §10 ("no limitarse a indicar que está mal").
//
// ## Vale para los TRES bloques
// Diagrama, pseudocódigo y Python producen la misma forma de resultado por
// caso, así que el mismo módulo sirve para los tres sin ramificar por bloque.

/**
 * Nivel máximo que la capa determinista puede alcanzar por sí sola (§10).
 * Del 5 en adelante (explicar el error concreto sobre EL CÓDIGO del alumno,
 * solución parcial, solución completa) hace falta la capa de IA (ADR-0006).
 */
export const NIVEL_MAXIMO_DETERMINISTA = 3;

/**
 * Lo que dicen los tres paneles de corrección cuando un fallo gasta el último
 * intento que sumaba puntos (`ya_no_puntua` del servidor, 2026-09-12). Vive
 * aquí porque los tres ya importan este módulo y la frase tiene que ser la
 * misma en los tres editores. No promete que se abra el siguiente: con el
 * bloque cerrado hasta terminar el anterior (DEC-153) no sería verdad.
 */
export const MENSAJE_SIN_INTENTOS =
  'Ya no te quedan intentos que sumen puntos en este ejercicio. Cuenta como terminado, y lo puedes seguir practicando cuando quieras.';

/**
 * Un caso ya corregido, en la forma común a los tres bloques.
 * @typedef {{
 *   entrada?: string,
 *   salidaEsperada?: string,
 *   salidaObtenida?: string,
 *   correcto?: boolean,
 * }} CasoCorregido
 */

/**
 * Material pedagógico público del ejercicio (`contenido_pedagogico`, DEC-015).
 * @typedef {{
 *   objetivos?: string[],
 *   conceptosPrevios?: string[],
 *   erroresFrecuentes?: string[],
 * }} MaterialPedagogico
 */

/**
 * Un peldaño de ayuda ya redactado. `cuesta` marca los que PENALIZAN la
 * puntuación: quien los muestre debe cobrarlos de verdad (llamando al endpoint
 * de pista, que es quien aplica el descuento en servidor) y avisar ANTES. Un
 * aviso de "esto te quita nota" que no descontara nada sería mentir al alumno.
 * @typedef {{ nivel: number, titulo: string, texto: string, cuesta: boolean }} NivelAyuda
 */

/**
 * Diagnostica EN QUÉ se diferencia la salida obtenida de la esperada, en
 * términos que el alumno pueda accionar ("no coinciden las mayúsculas") en vez
 * de dejarle comparar dos cadenas a ojo. Determinista y sin IA.
 *
 * Devuelve `null` si son iguales (no hay nada que diagnosticar).
 *
 * @param {string | undefined} obtenida
 * @param {string | undefined} esperada
 * @param {(texto: string) => string} [normalizar] regla con la que se corrige.
 *   Pseudocódigo y Python la pasan (`utils/comparar-salida.js`); diagramas,
 *   que comparan valores exactos, no.
 * @returns {string | null}
 */
export function diagnosticarDiferencia(obtenida, esperada, normalizar) {
  let o = typeof obtenida === 'string' ? obtenida : '';
  let e = typeof esperada === 'string' ? esperada : '';
  if (o === e) return null;
  if (normalizar) {
    // El corrector de texto no cuenta mayúsculas, tildes ni espacios repetidos
    // (DEC-119): se diagnostica sobre lo que SÍ cuenta, o la ayuda mandaría al
    // alumno a corregir justo lo que se ignora y le escondería el fallo real.
    const obtenidaNormalizada = normalizar(o);
    const esperadaNormalizada = normalizar(e);
    // Iguales con la regla general pero corregido como fallo: es un caso con
    // `letras_exactas` (DEC-120), donde las letras sí cuentan.
    if (obtenidaNormalizada === esperadaNormalizada) {
      return 'el texto es el correcto, pero en este ejercicio sí cuentan las MAYÚSCULAS y las tildes';
    }
    o = obtenidaNormalizada;
    e = esperadaNormalizada;
  }
  if (o === '') return 'tu algoritmo no llega a escribir nada';
  if (e === '') return 'no debería escribirse nada, pero tu algoritmo sí escribe';
  if (o.toLocaleLowerCase('es') === e.toLocaleLowerCase('es')) {
    return 'el texto es el correcto, pero no coinciden las MAYÚSCULAS y las minúsculas';
  }
  if (o.replace(/\s+/g, '') === e.replace(/\s+/g, '')) {
    return 'el contenido es correcto, pero los espacios o los saltos de línea no cuadran';
  }
  const numeroObtenido = Number(o.trim());
  const numeroEsperado = Number(e.trim());
  if (Number.isFinite(numeroObtenido) && Number.isFinite(numeroEsperado) && o.trim() !== '') {
    const relacion = numeroObtenido > numeroEsperado ? 'de más' : 'de menos';
    return `el resultado es un número, pero te sale ${numeroObtenido} en vez de ${numeroEsperado} (${relacion})`;
  }
  if (e.startsWith(o)) return 'lo que escribes está bien, pero se queda corto: falta el final';
  if (o.startsWith(e)) return 'la primera parte está bien, pero escribes cosas de más detrás';
  if (o.includes(e)) return 'la respuesta correcta está dentro de tu salida, con texto de sobra alrededor';

  let i = 0;
  while (i < o.length && i < e.length && o[i] === e[i]) i += 1;
  if (i > 0) return `tu salida empieza bien y se desvía a partir del carácter ${i + 1}`;
  return 'tu salida no se parece a la esperada desde el primer carácter';
}

/**
 * Normaliza una lista de cadenas: descarta lo que no sea texto con contenido.
 * @param {unknown} valor
 * @returns {string[]}
 */
function comoLista(valor) {
  if (!Array.isArray(valor)) return [];
  return valor.filter((item) => typeof item === 'string' && item.trim() !== '');
}

/**
 * Recorta un texto para que quepa en la interfaz sin romper el diseño, sin
 * cortar a mitad de palabra.
 * @param {string} texto
 * @param {number} [maximo]
 * @returns {string}
 */
export function recortar(texto, maximo = 160) {
  const limpio = texto.trim();
  if (limpio.length <= maximo) return limpio;
  const cortado = limpio.slice(0, maximo);
  const ultimoEspacio = cortado.lastIndexOf(' ');
  return `${(ultimoEspacio > maximo * 0.6 ? cortado.slice(0, ultimoEspacio) : cortado).trimEnd()}…`;
}

/**
 * Describe un valor de caso para mostrarlo en línea. Una salida vacía es un
 * dato REAL (el algoritmo no imprimió nada), no un hueco: se dice con palabras
 * en vez de dejar un espacio en blanco que el alumno no sabría interpretar.
 * @param {string | undefined} valor
 * @returns {string}
 */
export function describirValor(valor) {
  if (typeof valor !== 'string' || valor === '') return '(nada)';
  return recortar(valor.replace(/\n/g, ' ⏎ '), 80);
}

/**
 * Construye los niveles de ayuda para un resultado de corrección INCORRECTO.
 *
 * Devuelve lista VACÍA cuando no hay nada que explicar (todos los casos
 * correctos, o ningún caso): felicitar o inventar ayuda serían ambos ruido.
 *
 * `normalizar` es la regla con la que se corrige (ver `diagnosticarDiferencia`).
 *
 * @param {{ casos?: CasoCorregido[], normalizar?: (texto: string) => string } & MaterialPedagogico} datos
 * @returns {NivelAyuda[]}
 */
export function construirNivelesAyuda(datos) {
  const casos = Array.isArray(datos.casos) ? datos.casos : [];
  const fallidos = casos.filter((caso) => caso.correcto !== true);
  if (casos.length === 0 || fallidos.length === 0) return [];

  /** @type {NivelAyuda[]} */
  const niveles = [];

  // --- Nivel 1: existe un problema (sin decir dónde). ---------------------
  const total = casos.length;
  const fallan = fallidos.length;
  let textoNivel1;
  if (total === 1) {
    // Con un solo caso, "ninguno de los 1 casos" es agramatical.
    textoNivel1 =
      'El único caso de prueba no produce la salida esperada. Vuelve a leer el enunciado y repasa tu algoritmo de principio a fin.';
  } else if (fallan === total) {
    textoNivel1 = `Ninguno de los ${total} casos de prueba produce la salida esperada. Vuelve a leer el enunciado y repasa tu algoritmo de principio a fin.`;
  } else {
    textoNivel1 = `${fallan === 1 ? '1 caso de prueba no produce' : `${fallan} casos de prueba no producen`} la salida esperada (de ${total}). Los demás sí funcionan, así que la idea general es correcta: falla algún detalle.`;
  }
  niveles.push({ nivel: 1, titulo: 'Hay algo que revisar', texto: textoNivel1, cuesta: false });

  // --- Nivel 2: CONCRETO -- en qué se diferencia exactamente. -------------
  // No basta con "mira el caso 2": se diagnostica la diferencia (mayúsculas,
  // espacios, número, longitud…) para que el alumno sepa QUÉ buscar. Señalar
  // la línea del código exigiría un analizador (DEC-032), que no existe.
  const indicePrimerFallo = casos.findIndex((caso) => caso.correcto !== true);
  const primerFallo = casos[indicePrimerFallo];
  const diagnostico = diagnosticarDiferencia(
    primerFallo?.salidaObtenida,
    primerFallo?.salidaEsperada,
    datos.normalizar,
  );
  const cabecera = `Mira el caso ${indicePrimerFallo + 1} (entrada ${describirValor(primerFallo?.entrada)}): tú das ${describirValor(primerFallo?.salidaObtenida)} y se espera ${describirValor(primerFallo?.salidaEsperada)}.`;
  niveles.push({
    nivel: 2,
    titulo: 'Qué falla exactamente',
    texto: diagnostico === null ? cabecera : `${cabecera} Fíjate: ${diagnostico}.`,
    cuesta: false,
  });

  // --- Nivel 3: casi la solución. CUESTA PUNTOS. --------------------------
  // Aquí se deja de orientar y se dice el error concreto (los
  // `errores_frecuentes` que redactó quien creó el ejercicio, en afirmación,
  // no en pregunta) junto con lo que el ejercicio entrena. Por eso PENALIZA:
  // `cuesta: true` obliga a quien lo muestre a cobrarlo de verdad y a avisar
  // antes. Sin material pedagógico este peldaño NO se ofrece: cobrar por una
  // ayuda vacía sería estafar al alumno.
  const objetivos = comoLista(datos.objetivos);
  const conceptos = comoLista(datos.conceptosPrevios);
  const errores = comoLista(datos.erroresFrecuentes);
  const contexto = objetivos.length > 0 ? objetivos : conceptos;

  if (errores.length > 0 || contexto.length > 0) {
    const partes = [];
    if (errores.length > 0) {
      partes.push(
        `Casi seguro que es esto: ${errores.map((error) => recortar(error, 140)).join(' También: ')}`,
      );
    }
    if (contexto.length > 0) {
      partes.push(
        `Recuerda que este ejercicio va de: ${contexto.map((item) => recortar(item, 120)).join('; ')}.`,
      );
    }
    niveles.push({
      nivel: 3,
      titulo: 'Casi la solución',
      texto: partes.join(' '),
      cuesta: true,
    });
  }

  return niveles;
}
